import { useGroup } from '@gik/api/inkinds/group';
import { useInkind, useInkindNumSupporters } from '@gik/api/inkinds/inkind';
import { updateUserSeenPremiumPageReminders, useUser, useUserSeenPremiumPageReminders } from '@gik/api/users/user';
import { optimisticUpdate } from '@gik/core/api';
import { Breakpoint, useBreakpoint } from '@gik/core/hooks/hooks/BreakpointHooks';
import { useIsMounted } from '@gik/core/hooks/hooks/useIsMounted';
import { useInkindCan } from '@gik/core/store/permissions';
import { useUserStore } from '@gik/core/store/UserStore';
import bemBlock from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { checkRecipientAddressReminder } from '@gik/create/components/RecipientAddressReminder/openRecipientAddressReminder';
import { InkindPageInformation } from '@gik/inkind-page/components/InkindPageInformation/InkindPageInformation';
import { useInkindStore } from '@gik/inkind-page/store/InkindStore';
import isInkindPageRecipientInfoComplete from '@gik/inkind-page/utils/isInkindPageRecipientInfoComplete';
import userHasSeenInkindTourForThisPage from '@gik/inkind-page/utils/userHasSeenInkindTourForThisPage';
import { Container } from '@gik/ui/Container';
import { LayoutAside } from '@gik/ui/LayoutAside';
import moment from 'moment';
import React from 'react';
import useSearchParam from 'react-use/lib/useSearchParam';
import { InkindGroupWidget, useGroupVisibility } from '../InkindGroupWidget';
import InkindSupportersButton from '../InkindSupportersButton/InkindSupportersButton';
import InkindSupportSidebarContainer from '../InkindSupportSidebar/InkindSupportSidebarContainer';
import { ShareInkindButton } from '../ShareInkindButton/ShareInkindButton';
import { checkUpgradePageReminder } from '../UpgradePageReminder/UpgradePageReminder';
import useContactPageOrganizers from './useContactPageOrganizers';
import { CalendarSection } from '@gik/calendar';
import { WishlistGrid } from '@gik/wishlist-page/components/WishlistGrid';
import { WishlistSection } from './WishlistSection';
import { DynamicComponent } from '@gik/core/components/DynamicComponent';
import { InViewportFallback } from '@gik/core/components/InViewport/InViewportFallback';
import { Heading } from '@gik/ui/Heading';
import { DiscussionEmbed } from 'disqus-react';
import { StreamPosts } from '@gik/stream/components/Posts/StreamPosts';
import { InViewport } from '@gik/core/components/InViewport';
import { RelatedArticlesSection } from '@gik/ui/gik/RelatedArticles';

const blockName = 'inkind-page-content';

export type InkindPageContentProps = {
  isCalendarEnabled?: boolean;
  isWishlistEnabled?: boolean;
  showSuggestedItemsDisableButton?: boolean;
};

function InkindPageContentComp({ showSuggestedItemsDisableButton, ...props }: InkindPageContentProps): JSX.Element {
  const bem = bemBlock(blockName);
  const isMdDown = useBreakpoint(Breakpoint.MD_DOWN);
  const inkindRouteId = useInkindStore(state => state.inkindRouteId);
  const { data: inkindPage } = useInkind(inkindRouteId, { revalidateOnMount: true });
  const { data: numSupportersRaw } = useInkindNumSupporters(inkindRouteId, { revalidateOnMount: true });
  const numSupporters = parseInt(numSupportersRaw);

  useContactPageOrganizers(inkindRouteId);
  const userId = useUserStore(state => state.id);
  const { data: user } = useUser(userId);
  const { data: seenPremiumPageReminders, mutate: mutateUserSeenPremiumPageReminders } =
    useUserSeenPremiumPageReminders(userId);
  const isMounted = useIsMounted();
  const canEdit = useInkindCan('manage', inkindRouteId, inkindPage?.groupId) && isMounted();

  const groupSlug = inkindPage?.groupId;
  const { data: group } = useGroup(groupSlug);
  const isGroupVisible = useGroupVisibility(group);

  const hasShareButton = canEdit || inkindPage?.allowPageSharing;
  const hasSupportersButton = canEdit;

  const inkindShareButton = hasShareButton && (
    <div className="tw-mb-8">
      <ShareInkindButton
        inkindRouteId={inkindRouteId}
        variant={!inkindPage?.allowPageSharing ? 'secondary' : 'primary'}
      />
      {!inkindPage?.allowPageSharing && (
        <div className="tw-text-center tw-text-sm tw-text-neutral-700 tw-mt-1">Only you can see this Share button</div>
      )}
    </div>
  );
  const inkindSupportersButton = hasSupportersButton && (
    <InkindSupportersButton num={numSupporters || 0} displayNumSupporters={true} />
  );

  const tourForceOpen = useSearchParam('tour');
  const openCommentId = useSearchParam('commentId');

  function asideHeightChanged() {
    const asideEl = document.querySelectorAll(
      '.gik-inkind-page-content__page-content-aside > div'
    )?.[0] as HTMLDivElement;

    const calendarWrapperEl = document.querySelectorAll('.gik-inkind-calendar')?.[0] as HTMLDivElement;

    const defaultMaxHeight = 590;

    if (window.innerWidth < 768 && calendarWrapperEl) {
      calendarWrapperEl.style.maxHeight = defaultMaxHeight + 'px';
      return undefined;
    }

    const asideHeight = asideEl?.offsetHeight || 0;

    const pageInfoEl = document.querySelectorAll(
      '.gik-inkind-page-content__inkind-page-information'
    )?.[0] as HTMLDivElement;

    const calendarHeaderEl = document.querySelectorAll('.gik-inkind-calendar-section__header')?.[0] as HTMLDivElement;

    let maxHeight = asideHeight - (pageInfoEl?.offsetHeight ?? 0) - (calendarHeaderEl?.offsetHeight ?? 0) - 36;
    if (maxHeight < defaultMaxHeight) maxHeight = defaultMaxHeight;

    if (calendarWrapperEl) calendarWrapperEl.style.setProperty('--aside-height', maxHeight + 'px');

    return asideEl?.offsetHeight;
  }

  React.useEffect(() => {
    if (window.location.href.indexOf('test-error-boundary=1') >= 0) {
      throw new Error('TEST!');
    }

    const asideEl = document.querySelectorAll('.gik-inkind-page-content__page-content-aside')?.[0] as HTMLDivElement;
    const observer = new ResizeObserver(asideHeightChanged);
    observer.observe(asideEl);

    return () => {
      observer.disconnect();
    };
  }, []);

  const scrollToComments = React.useCallback(() => {
    const commentsSectionEl = document.querySelector('#inkind-comments');

    if (!commentsSectionEl) {
      setTimeout(scrollToComments, 500);
      return;
    }

    if (commentsSectionEl && openCommentId) {
      scrollIntoView(commentsSectionEl as HTMLElement, {
        offset: 190,
      });
      /*scroller.scrollTo(`inkind-comments`, {
        spy: true,
        smooth: true,
      });*/
    }
  }, [openCommentId]);

  const scrollToCommentsTimeout = React.useRef<NodeJS.Timeout>();
  React.useEffect(() => {
    if (openCommentId) {
      scrollToCommentsTimeout.current = setTimeout(() => {
        scrollToComments();
      }, 1000);
    }
    return () => {
      clearTimeout(scrollToCommentsTimeout.current);
    };
  }, [openCommentId, scrollToComments]);

  // TODO: move these to inkind page after migration
  if (
    inkindPage &&
    isMounted() &&
    user &&
    canEdit &&
    userHasSeenInkindTourForThisPage(user, inkindPage) &&
    !tourForceOpen
  ) {
    !isInkindPageRecipientInfoComplete(inkindPage) &&
      checkRecipientAddressReminder(inkindRouteId, moment.utc(inkindPage.createdUtc));

    !inkindPage.isPremium &&
      seenPremiumPageReminders?.indexOf(inkindPage.routeId) < 0 &&
      checkUpgradePageReminder(moment.utc(inkindPage.createdUtc), () => {
        optimisticUpdate([...seenPremiumPageReminders, inkindRouteId], null, mutateUserSeenPremiumPageReminders, () =>
          updateUserSeenPremiumPageReminders(userId, inkindRouteId)
        );
      });
  }

  const isCalendarEnabled = props.isCalendarEnabled !== undefined ? props.isCalendarEnabled : inkindPage?.isCalendar;
  const isWishlistEnabled = props.isCalendarEnabled !== undefined ? props.isWishlistEnabled : inkindPage?.wishList;

  return (
    <div className={bem()}>
      <Container className={bem('content-container')}>
        <LayoutAside gutter="0" asideWidth="310px" className={bem('layout-aside')}>
          <main className={bem('main')}>
            {isMounted() && isMdDown && inkindSupportersButton}
            {isMounted() && isMdDown && inkindShareButton}

            {/* component itself has padding-x=15px, mb=30px, so here 0 margins, 0 paddings */}
            <section className={bem('inkind-page-information')}>
              <InkindPageInformation inkindRouteId={inkindRouteId} />
            </section>

            {isMounted() && isMdDown && (
              <>
                <InkindSupportSidebarContainer inkindRouteId={inkindRouteId} />
                <br />
              </>
            )}

            {/* if the calendar is disabled show the wishlist regardless if it's disabled too */}
            {isCalendarEnabled ? (
              <section className={bem('calendar-container')} id="inkind-calendar">
                <CalendarSection inkindRouteId={inkindRouteId} />
              </section>
            ) : (
              <section className={bem('wishlist-page')}>
                {(isMounted() && !isMdDown) || !isMounted() ? (
                  <WishlistGrid inkindRouteId={inkindRouteId} isFullWishlistPage={false} cols={4} />
                ) : (
                  <WishlistSection inkindRouteId={inkindRouteId} />
                )}
              </section>
            )}
          </main>
          <aside className={bem('page-content-aside')}>
            <div>
              {((isMounted() && !isMdDown) || !isMounted()) && (
                <section className={bem('right-sidebar-container')}>
                  {inkindSupportersButton}
                  {inkindShareButton}
                  <InkindSupportSidebarContainer inkindRouteId={inkindRouteId} />
                </section>
              )}
              {isCalendarEnabled && isWishlistEnabled && (
                <InViewport height={436}>
                  <WishlistSection inkindRouteId={inkindRouteId} />
                </InViewport>
              )}
              {(inkindPage ? inkindPage.enableSuggestedArticles : true) && (
                <InViewport height={436}>
                  <RelatedArticlesSection inkindRouteId={inkindRouteId} showDisable={showSuggestedItemsDisableButton} />
                </InViewport>
              )}
              {isGroupVisible && <InkindGroupWidget group={group} />}
            </div>
          </aside>
        </LayoutAside>
      </Container>

      {isMounted() && inkindPage?.allowDiscussions && inkindPage?.commentSystemVersion === '1' && (
        <DynamicComponent height={426} loadingComponent={() => <InViewportFallback className={bem('comments')} />}>
          <section className={bem('comments')}>
            <Heading>KIND WORDS & COMMENTS</Heading>
            <DiscussionEmbed shortname="giveinkind" config={{}} />
          </section>
        </DynamicComponent>
      )}

      {isMounted() && inkindPage?.allowDiscussions && inkindPage?.commentSystemVersion === '2' && (
        <StreamPosts inkindRouteId={inkindRouteId} isPageOwnerOrOrganizer={canEdit} id="inkind-messages" />
      )}
    </div>
  );
}

export const InkindPageContent = withComponentErrorBoundary(InkindPageContentComp);
